import React from "react";
import { Chart, Tooltip } from "@highcharts/react";
import { Pie } from "@highcharts/react/series";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import { AssetTotalByConnectionStatus } from "../../graphql/queries/assetTotalByConnectionStatus";
import { CircularProgress, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { ActionsMenu } from "@dreebit-registry/vsm-microservice-components";
import { WidgetActionsMenu } from "../WidgetActionsMenu";
import { useNavigate } from "react-router-dom";
import Highcharts from "highcharts";
import { IOT_STATUS_CONFIG } from "../../config/iotConnectionStatusConfig";

interface IotAssetsStatusWidgetProps {
  onDelete: () => void;
  onEdit: () => void;
}

const IotAssetsStatusWidget: React.FC<IotAssetsStatusWidgetProps> = ({
  onDelete,
  onEdit,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const actionItems = WidgetActionsMenu(onDelete, onEdit);

  const handleSegmentClick = (status: string) => {
    navigate(`/assets?page=0&pageSize=25&iotConnectionStatus=${status}`);
  };

  const iotConnectionStatus = useQuery(AssetTotalByConnectionStatus, {
    variables: {
      runningFilter: { iotConnectionStatus: { equals: "RUNNING" } },
      warningFilter: { iotConnectionStatus: { equals: "WARNING" } },
      errorFilter: { iotConnectionStatus: { equals: "ERROR" } },
      dataBufferingFilter: { iotConnectionStatus: { equals: "DATA_BUFFERING" } },
      offFilter: { iotConnectionStatus: { equals: "OFF" } },
      currentlyOfflineFilter: { iotConnectionStatus: { equals: "CURRENTLY_OFFLINE" } },
      neverConnectedFilter: { iotConnectionStatus: { equals: "NEVER_CONNECTED" } },
    },
    pollInterval: 30000,
  });

  if (iotConnectionStatus.loading) {
    return <CircularProgress />;
  }
  if (iotConnectionStatus.error) {
    return <p>Error loading data.</p>;
  }

  const statusCountMap: Record<string, number> = {
    RUNNING: _.get(iotConnectionStatus, "data.running.total", 0),
    WARNING: _.get(iotConnectionStatus, "data.warning.total", 0),
    ERROR: _.get(iotConnectionStatus, "data.error.total", 0),
    DATA_BUFFERING: _.get(iotConnectionStatus, "data.dataBuffering.total", 0),
    OFF: _.get(iotConnectionStatus, "data.off.total", 0),
    CURRENTLY_OFFLINE: _.get(iotConnectionStatus, "data.currentlyOffline.total", 0),
    NEVER_CONNECTED: _.get(iotConnectionStatus, "data.neverConnected.total", 0),
  };

  const activeStatuses = IOT_STATUS_CONFIG.filter(
    (cfg) => statusCountMap[cfg.status] > 0
  );

  const pieData = activeStatuses.map((cfg) => ({
    name: t(cfg.label),
    y: statusCountMap[cfg.status],
  }));

  const pieColors = activeStatuses.map((cfg) => cfg.color);

  const statusByLabel: Record<string, string> = Object.fromEntries(
    IOT_STATUS_CONFIG.map(({ label, status }) => [t(label), status])
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography level="titleSmallLight" sx={{ flex: 1, textAlign: "left", paddingLeft: "10px", color: "#333" }}>
          {t("IoT Assets")}
        </Typography>
        <div className="no-drag">
          <ActionsMenu items={actionItems} />
        </div>
      </div>
      {/* Legend and pie share the row instead of the legend floating over the
          chart: status labels are full sentences ("Asset is turned off") and
          used to run underneath the pie. */}
      <div className="no-drag" style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 4,
            paddingLeft: 12,
            paddingRight: 8,
            maxWidth: "50%",
            flexShrink: 0,
          }}
        >
          {activeStatuses.map((cfg) => (
            <div
              key={cfg.status}
              style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
              onClick={() => handleSegmentClick(cfg.status)}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: cfg.color,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: "0.75rem", color: "#333" }}>
                {`${t(cfg.label)} (${statusCountMap[cfg.status]})`}
              </span>
            </div>
          ))}
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Chart
            options={{
              credits: { enabled: false },
              chart: {
                margin: 0,
                spacing: [0, 0, 0, 0],
                animation: false,
                events: {
                  load() {
                    const chart = this;
                    setTimeout(() => chart.reflow(), 100);
                  },
                },
              },
            }}
            containerProps={{
              style: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 },
            }}
          >
            <Tooltip pointFormat="<b>{point.y}</b>" />
            <Pie.Series
              options={{
                animation: false,
                showInLegend: false,
                size: "80%",
                center: ['50%', '50%'],
                name: "IoT Assets",
                data: pieData,
                colors: pieColors,
                dataLabels: {
                  enabled: false,
                },
                cursor: "pointer",
                point: {
                  events: {
                    click: function (this: Highcharts.Point) {
                      const status = statusByLabel[this.name];
                      if (status) handleSegmentClick(status);
                    },
                  },
                },
              }}
            />
          </Chart>
        </div>
      </div>
    </div>
  );
};

export default IotAssetsStatusWidget;
